import { useCallback, useMemo } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { Section } from '@/components/container/section/Section';
import { GoldDataSourceSchemaValues } from '@/zod/schemas/dataSource/Gold';
import { AddTableSchemaValues } from '@/zod/schemas/dataSource/Common';
import { AddTable } from '@/components/dataSources/addTable/AddTable';
import { useConfigDataSource } from '@/contexts/ConfigDataSourceContext';
import { TocWrapper } from '@/components/tableOfContents/ToCWrapper';
import { GoldDataPreviewContainer } from '@/components/dataSources/dataPreviewContainer/gold/GoldDataPreviewContainer';

const emptyAddTable: AddTableSchemaValues = {
  name: '',
  customFunction: '',
  customFunctionOptions: [],
  filter: '',
  postFilter: '',
  fields: [],
  jsonExtract: [],
  preserve: false,
  omitColumns: [],
  duplicatePrefix: '',
  embedColumn: '',
  embed: false,
  sourceTable: '',
  enableOverrideLiquidColumns: false,
  overrideLiquidColumns: [],
  tableMode: 'configure',
  temporaryFields: [],
};

export const GoldAddTables = () => {
  const formik = useFormikContext<GoldDataSourceSchemaValues>();
  const { enableDatasourcePreview } = useFlags<FlagSet>();

  const { dataSource } = useConfigDataSource();

  const { values } = formik;

  const createAddTable = useCallback(() => ({ ...emptyAddTable }), []);

  const sourceTableNames = useMemo(() => {
    const addTableNames =
      dataSource.spec.silver?.transform?.presetOverrides?.addTables?.map(
        (addTable) => addTable.name || '',
      ) || [];
    const modifyTableNames =
      dataSource.spec.silver?.transform?.presetOverrides?.modifyTables?.map(
        (modifyTable) => modifyTable.name || '',
      ) || [];
    return [...addTableNames, ...modifyTableNames];
  }, [dataSource]);

  return (
    <div>
      <FieldArray name="addTables">
        {({ remove, push }) => (
          <>
            <Button
              leftIcon="radix-icons:plus"
              variant="default"
              className="w-fit"
              onClick={() => push(createAddTable())}
              type="button"
              data-testid="add-table-button-top"
            >
              Add a table
            </Button>

            {!!values.addTables.length && <Spacer size="lg" />}

            {values.addTables.map((addTable, index) => {
              const prefix = `addTables.${index}.`;
              return (
                <div key={prefix}>
                  <TocWrapper
                    name={`addTables.${index}`}
                    displayName={addTable.name || `Table ${index + 1}`}
                  >
                    {!!index && <Spacer size="lg" />}
                    <Section className="relative">
                      <div className="absolute right-5 top-5 flex items-center gap-2">
                        {enableDatasourcePreview && (
                          <GoldDataPreviewContainer
                            tableIndex={index}
                            isModifyTable={false}
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="small"
                          iconOnly
                          leftIcon="antimatterhq:trash"
                          onClick={() => remove(index)}
                        />
                      </div>

                      <AddTable
                        index={index}
                        currentTable="gold"
                        previousTable="silver"
                        table={addTable}
                        sourceTableNames={sourceTableNames}
                      />
                    </Section>
                  </TocWrapper>
                </div>
              );
            })}

            {!!values.addTables.length && (
              <>
                <Spacer size="lg" />

                <Button
                  leftIcon="radix-icons:plus"
                  variant="default"
                  className="w-fit"
                  onClick={() => push(createAddTable())}
                  type="button"
                  data-testid="add-table-button-bottom"
                >
                  Add a table
                </Button>
              </>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};
